import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import HistoryIcon from '@mui/icons-material/History';
import CommentIcon from '@mui/icons-material/Comment';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { TiTicket } from 'react-icons/ti';

import {
  useGetGestionTicket,
  useFetchTicketHistorial,
  useFetchUsuarioResumen,
  useReabrirTicket,
  useVision360Cliente,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { ITicketHistorial, IVision360Data } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useAuthStore } from '@/store/auth';
import { hasSystemModule } from '@/shared/utils';
import { SystemModulesEnum } from '@/auth/interfaces/system-modules.enum';
import FormCrearTicket from '../../nuevo-ticket/shared/components/FormCrearTicket/FormCrearTicket';

import HeaderTicket from './HeaderTicket';
import InfoTicket from './InfoTicket';
import CardCliente from './CardCliente';
import TimelineHistorial from './TimelineHistorial';
import DatosTecnicosCard from './DatosTecnicosCard';
import FormComentario from './FormComentario';
import ModalEscalar from './ModalEscalar';
import ModalAsignar from './ModalAsignar';
import ModalCerrar from './ModalCerrar';
import { WifiTethering } from '@mui/icons-material';
import { TypographyText } from './TypographyText';
import TicketVisitaDetail from './TicketVisitaDetail';

interface TicketDetailContentProps {
  ticketId: string;
  /** Si se pasa, se usa en vez de navigate(-1) en el boton "Volver". */
  onBack?: () => void;
  /** Si true, oculta el wrapper Paper externo (util cuando ya esta dentro de un Dialog). */
  embedded?: boolean;
  /** Si true, modo SOLO LECTURA: oculta acciones (escalar/asignar/estado/comentarios/reabrir). */
  readOnly?: boolean;
}

export default function TicketDetailContent({ ticketId, onBack, embedded, readOnly }: TicketDetailContentProps) {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));

  const { data: ticketDataResp, isLoading: isLoadingTicket } = useGetGestionTicket(ticketId);
  const { data: historialrResp, isLoading: isLoadingHistorial } = useFetchTicketHistorial(ticketId);
/*   const {
    data: ordenTrabajo,
    isLoading:isLoadingTrabajo
  }=useFetchOrdenTrabajos({
    params:{
      id_ticket:ticketId,
      estado_orden_trabajo:'FINALIZADO'
    }
  })
  console.log(ordenTrabajo) */
  // IMPORTANTE: las dependencias eran [isLoadingHistorial]/[isLoadingTicket]
  // pero cuando React Query refetchea, isLoading queda en false y data cambia
  // -> el useMemo NO se recalculaba y la UI seguia mostrando data vieja hasta
  // que el usuario hiciera F5 (que reseteaba isLoading a true y disparaba el
  // recalculo). Las dependencias correctas son las respuestas mismas.
  const historialData = useMemo(() => {
    return historialrResp?.data || [];
  }, [historialrResp]);

  const ordenServicioData = useMemo(() => {
    return ticketDataResp?.data?.orden_servicio;
  }, [ticketDataResp]);

  const ticketData = useMemo(() => {
    return ticketDataResp?.data?.ticket;
  }, [ticketDataResp]);


  const [openEscalar, setOpenEscalar] = useState(false);
  const [openAsignar, setOpenAsignar] = useState(false);
  const [openCerrar, setOpenCerrar] = useState(false);


  const user = useAuthStore((s) => s.user);
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const isSuperUser = !!(user as any)?.is_superuser;
  const { data: resumenRes, isLoading: isLoadingPermisos } = useFetchUsuarioResumen(
    currentUserId,
    !!currentUserId
  );
  const _resRaw = resumenRes as any;
  const userPermisos: string[] =
    _resRaw?.data?.data?.permisos ||
    _resRaw?.data?.permisos ||
    _resRaw?.permisos ||
    [];

  const canEscalar = isSuperUser || userPermisos.includes('tickets.escalar_ticket');
  const canAsignar = isSuperUser || userPermisos.includes('tickets.asignar_ticket');
  const canCerrar = isSuperUser || userPermisos.includes('tickets.cerrar_ticket');
  const canComentar = isSuperUser || userPermisos.includes('tickets.comentar_ticket');
  const canReabrir = isSuperUser || userPermisos.includes('tickets.reabrir_ticket');
  // Admin de tickets: superuser o quien tenga el permiso configurable
  // 'tickets.ver_todos_tickets' (lo asigna /parametrizacion/gestion-accesos).
  // Este perfil hace bypass del workflow: ve y opera (cerrar, cambiar
  // estado) sobre cualquier ticket sin importar el area ni si esta
  // asignado. El backend tambien hace bypass para este permiso.
  const isAdminTickets = isSuperUser || userPermisos.includes('tickets.ver_todos_tickets');

  // Boton "+" junto a "Requiere Visita": crea un ticket de visita para el
  // mismo cliente/contrato desde un ticket existente que NO nacio como
  // visita. Mismo gate que el checkbox "¿Requiere Visita?" del form de
  // creacion (FormCrearTicket): permiso de rol
  // 'tickets.gestionar_orden_servicio' (Editar Rol del modulo Tickets) O el
  // modulo de sistema VISITAS.REQUIERE_VISITA (Gestion de Accesos >
  // Modulos). Retrocompatible: si el usuario aun no tiene ningun permiso
  // tickets.* cargado, se muestra.
  const tieneAlgunPermisoOS =
    userPermisos.includes('tickets.gestionar_orden_servicio') ||
    userPermisos.some((p) => p.startsWith('tickets.'));
  const canCrearVisitaDesdeTicket =
    isSuperUser ||
    !tieneAlgunPermisoOS ||
    userPermisos.includes('tickets.gestionar_orden_servicio') ||
    hasSystemModule(SystemModulesEnum.VISITAS_REQUIERE_VISITA);

  const [openCrearVisita, setOpenCrearVisita] = useState(false);
  // Vision 360 del cliente del ticket (lazy: solo al presionar el "+").
  // Reusa el endpoint del flujo normal de creacion para que el modal se
  // abra con los MISMOS datos de cliente y contrato que el original.
  const { data: v360Res, isLoading: isLoadingV360 } = useVision360Cliente(
    (ticketData as any)?.cliente || '',
    (ticketData as any)?.contrato || undefined,
    openCrearVisita && !!(ticketData as any)?.cliente
  );
  const v360Data: IVision360Data | null =
    ((v360Res?.data as any)?.data as IVision360Data) || (v360Res?.data as any) || null;
  const contratoVisita = useMemo(() => {
    const contratos = (v360Data as any)?.contratos || [];
    if (!contratos.length) return null;
    return (
      contratos.find(
        (c: any) => String(c.id_contrato) === String((ticketData as any)?.contrato)
      ) || contratos[0]
    );
  }, [v360Data, ticketData]);


  // localidad

  if (isLoadingTicket) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const ticket = ticketData;

  if (!ticket) {
    return (
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Ticket no encontrado
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          {embedded ? 'Cerrar' : 'Volver'}
        </Button>
      </Paper>
    );
  }

  const historial: ITicketHistorial[] = historialData ?? [];
  const estadoData: any = ticket.estado_ticket_data;
  const isClosed = estadoData?.es_final === true
    || estadoData?.codigo === 'cerrado'
    || estadoData?.codigo === 'cancelado';

  return (
    <>
      <Paper elevation={embedded ? 0 : 2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={embedded ? 'Cerrar' : 'Volver'}>
              <IconButton onClick={handleBack} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h4"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TiTicket />
              DETALLE DEL TICKET
            </Typography>
          </Box>

          {!readOnly && !isClosed && !isLoadingPermisos && (canEscalar || canAsignar || canCerrar) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canEscalar && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  startIcon={<ArrowUpwardIcon />}
                  onClick={() => setOpenEscalar(true)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Escalar
                </Button>
              )}
              {canAsignar && (
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={<AssignmentIndIcon />}
                  onClick={() => setOpenAsignar(true)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {ticket.usuario_asignado_id ? 'Reasignar' : 'Asignar'}
                </Button>
              )}
              {(() => {
                // Flujo simplificado de estados: Nuevo -> Abierto -> Cerrado.
                //   - Nuevo -> Abierto lo hace la Asignacion (backend).
                //   - La unica transicion manual es el cierre: boton
                //     "Cerrar Ticket" -> ModalCerrar (solucion + categoria y
                //     subcategoria de cierre del arbol del area).
                //   - El boton aparece cuando el ticket esta en flujo
                //     (asignado y no 'nuevo'), igual que el selector anterior.
                //   - EXCEPCION admin: superuser o 'tickets.ver_todos_tickets'
                //     lo ven siempre (incluso ticket nuevo sin asignar),
                //     para resolver casos atipicos sin asignarse el ticket.
                const codigoEstado = (ticket.estado_ticket_data?.codigo || '').toLowerCase();
                const enFlujo = codigoEstado !== 'nuevo' && !!ticket.usuario_asignado_id;
                if (!canCerrar) return null;
                if (!enFlujo && !isAdminTickets) return null;
                return (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<TaskAltIcon />}
                    onClick={() => setOpenCerrar(true)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Cerrar Ticket
                  </Button>
                );
              })()}
            </Box>
          )}

          {!readOnly && isClosed && !isLoadingPermisos && canReabrir && (
            <ReabrirButton ticketId={ticketId} />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <HeaderTicket ticket={ticket} />
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <InfoTicket ticket={ticket} />

          {(ticket.contacto_nombre || ticket.contacto_telefono) && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 1 }}
              >
                <ContactPhoneIcon fontSize="small" />
                Informacion de Contacto
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Nombre</Typography>
                  <Typography variant="body2" fontWeight={500}>{ticket.contacto_nombre || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Telefono</Typography>
                  <Typography variant="body2" fontWeight={500}>{ticket.contacto_telefono || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Tipo Contacto</Typography>
                  <Typography variant="body2" fontWeight={500}>{ticket.contacto_tipo || '-'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <DatosTecnicosCard datosTecnicos={(ticket as any).datos_tecnicos_snapshot} />

          {ticket.solucion && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f1f8e9' }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', mb: 1 }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
                Solucion
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Categoria + Subcategoria de cierre (V1_19_0). Compat con el
                  modelo viejo: si el ticket fue cerrado antes de V1_19 se
                  muestra el campo viejo subcategoria_cierre_data. */}
              {(() => {
                const t = ticket as any;
                const cierreCat = t.cierre_categoria_data;
                const cierreSub = t.cierre_subcategoria_data;
                const subCierreViejo = t.subcategoria_cierre_data;
                if (!cierreCat && !cierreSub && !subCierreViejo) return null;
                return (
                  <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
                    {cierreCat && (
                      <Chip
                        label={`Categoria: ${cierreCat.nombre}`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {cierreSub && (
                      <Chip
                        label={`Subcategoria: ${cierreSub.nombre}`}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {!cierreCat && !cierreSub && subCierreViejo && (
                      <Chip
                        label={`Motivo: ${subCierreViejo.nombre}`}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                );
              })()}

              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {ticket.solucion}
              </Typography>
              {ticket.fecha_cierre && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Cerrado: {new Date(ticket.fecha_cierre).toLocaleString('es-EC', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </Typography>
              )}
            </Paper>
          )}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 1 }}
            >
              <WifiTethering fontSize="small" />
              Orden de Servicio
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {!ordenServicioData || ordenServicioData?.tipo_orden_servicio !== "VISITA" ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No existe orden de servicio registrada para este ticket.
              </Alert>
            ) : null}
            {ordenServicioData?.tipo_orden_servicio === "VISITA" && (
              <Grid container spacing={2}>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Tipo de servicio</Typography>
                  <Typography variant="body2" fontWeight={500}>{ordenServicioData?.tipo_orden_servicio || '-'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Dirección</Typography>
                  <Typography variant="body2" fontWeight={500}>{ordenServicioData?.visita?.direccion || '-'}</Typography>
                </Grid>


                <TypographyText
                  label="País"
                  value={ordenServicioData?.visita?.pais || '-'}
                />
                <TypographyText
                  label="Provincia"
                  value={ordenServicioData?.visita?.provincia || '-'}
                />

                <TypographyText
                  label="Ciudad"
                  value={ordenServicioData?.visita?.ciudad || '-'}
                />

                <TypographyText
                  label="Zona"
                  value={ordenServicioData?.visita?.zona || '-'}
                />

                <TypographyText
                  label="Parroquia"
                  value={ordenServicioData?.visita?.parroquia || '-'}
                />
                <TypographyText
                  label="Fecha Visita"
                  value={
                    ordenServicioData?.visita?.fecha_visita
                      ? new Date(ordenServicioData.visita.fecha_visita).toLocaleString('es-EC', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'
                  }
                />
                <TypographyText
                  label="Observaciones para el técnico"
                  value={ordenServicioData?.visita?.observaciones || '-'}
                />

              </Grid>
            )}

          </Paper>



          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 1 }}
            >
              <HistoryIcon fontSize="small" />
              Historial de Actividad
              <Chip label={historial.length} size="small" color="primary" sx={{ ml: 1 }} />
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {isLoadingHistorial ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
               <TicketVisitaDetail ticketId={ticketId}/>
              <TimelineHistorial historial={historial} ticketId={ticketId} />
              </>
             
            )}
          </Paper>

          {!readOnly && !isClosed && !isLoadingPermisos && canComentar && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 1 }}
              >
                <CommentIcon fontSize="small" />
                Agregar Comentario
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <FormComentario ticketId={ticketId} />
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <CardCliente ticket={ticket} />

          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
              Detalles Adicionales
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Es Masivo</Typography>
                <Box>
                  <Chip
                    label={ticket.es_masivo ? 'Si' : 'No'}
                    size="small"
                    color={ticket.es_masivo ? 'error' : 'default'}
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Requiere Visita</Typography>
                  {!readOnly && !isClosed && canCrearVisitaDesdeTicket && !!ticket.cliente && (
                    <Tooltip title="Crear un ticket de visita para este cliente (abre el formulario con ¿Requiere Visita? activado)">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          sx={{ p: 0.25 }}
                          onClick={() => setOpenCrearVisita(true)}
                          disabled={openCrearVisita && isLoadingV360}
                        >
                          {openCrearVisita && isLoadingV360 ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AddCircleOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  <Chip
                    label={ticket.requiere_visita ? 'Si' : 'No'}
                    size="small"
                    color={ticket.requiere_visita ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Ticket Rapido</Typography>
                <Box>
                  <Chip
                    label={ticket.es_ticket_rapido ? 'Si' : 'No'}
                    size="small"
                    color={ticket.es_ticket_rapido ? 'info' : 'default'}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Flujo "+": FormCrearTicket precargado con el cliente/contrato de
          este ticket y "¿Requiere Visita?" ya activado. Al crear la visita,
          el form agrega un comentario de referencia en ESTE ticket (prop
          ticketOrigen). */}
      {openCrearVisita && v360Data && (
        <FormCrearTicket
          open
          onClose={() => setOpenCrearVisita(false)}
          cliente={(v360Data as any).cliente}
          contrato={contratoVisita}
          hasMasivoActivo={((v360Data as any).masivos_activos || []).length > 0}
          defaultRequiereVisita
          ticketOrigen={{ id: ticketId, codigo: (ticket as any).codigo_ticket }}
        />
      )}
      <ModalEscalar
        open={openEscalar}
        onClose={() => setOpenEscalar(false)}
        ticketId={ticketId}
      />
      <ModalAsignar
        open={openAsignar}
        onClose={() => setOpenAsignar(false)}
        ticketId={ticketId}
        areaDestinoId={ticket.area_destino}
        areaDestinoNombre={ticket.area_destino_data?.nombre}
        usuarioAsignadoActual={ticket.usuario_asignado_nombre || undefined}
      />
      <ModalCerrar
        open={openCerrar}
        onClose={() => setOpenCerrar(false)}
        ticketId={ticketId}
        areaDestinoId={ticket.area_destino}
        ticket={ticket}
      />
    </>
  );
}

function ReabrirButton({ ticketId }: { ticketId: string }) {
  const reabrirMutation = useReabrirTicket();
  return (
    <Button
      variant="contained"
      color="success"
      size="small"
      startIcon={<ReplayIcon />}
      onClick={() => reabrirMutation.mutate(ticketId)}
      disabled={reabrirMutation.isPending}
      sx={{ textTransform: 'none', fontWeight: 500 }}
    >
      {reabrirMutation.isPending ? 'Reabriendo...' : 'Reabrir Ticket'}
    </Button>
  );
}
